import { useContext, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, Divider, IconButton, List, Paper, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LanguageIcon from '@mui/icons-material/Language';

import { AuthStoreContext } from '../../Root';
import AdminUserStore from '../../../stores/admin-user-store';
import { DbNode } from '../../../types/node';
import { DbUser, UserRole } from '../../../types/user';
import NodeView from '../../dashboard/node/NodeView';
import { nodeIcon, NodeChildrenSkeleton, NodeLoadError } from '../../dashboard/node/NodeItem';
import { UserDndContext, useUserDnd } from '../../../contexts/UserDndContext';
import UserService from '../../../http/user-service';
import AddUserDialog from './AddUserDialog';
import EditUserDialog from './EditUserDialog';
import DeleteUserDialog from './DeleteUserDialog';
import SetPasswordDialog from './SetPasswordDialog';
import MoveUserDialog from './MoveUserDialog';

interface PendingMove {
    user: DbUser;
    targetNodeId: number;
    targetNodeName: string;
}

//=================================================================================================
interface UserItemProps {
    user: DbUser;
    depth: number;
    onChanged?: () => void;
}

function UserItem({ user, depth, onChanged }: UserItemProps) {
    const { authStore } = useContext(AuthStoreContext);
    const dnd = useUserDnd();
    const [editOpen, setEditOpen] = useState(false);
    const [passOpen, setPassOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);

    const actions = (
        <>
            <Tooltip title="Редактировать">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setEditOpen(true);
                    }}
                >
                    <EditIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Задать пароль">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setPassOpen(true);
                    }}
                >
                    <LockIcon fontSize="small" />
                </IconButton>
            </Tooltip>
            <Tooltip title="Удалить">
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        setDeleteOpen(true);
                    }}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </Tooltip>
        </>
    );

    const icon =
        user.type === UserRole.Administrator ? (
            <AdminPanelSettingsIcon fontSize="small" />
        ) : (
            <PersonIcon fontSize="small" />
        );

    const view = (
        <>
            <NodeView
                name={user.login}
                description={user.email ?? user.description ?? undefined}
                icon={icon}
                actions={actions}
                depth={depth}
            />
            <EditUserDialog
                user={user}
                open={editOpen}
                onClose={() => setEditOpen(false)}
                onSaved={() => {
                    setEditOpen(false);
                    onChanged?.();
                }}
                rootNodeId={authStore.user?.node_id ?? 0}
            />
            <SetPasswordDialog user={user} open={passOpen} onClose={() => setPassOpen(false)} />
            <DeleteUserDialog
                user={user}
                open={deleteOpen}
                onClose={() => setDeleteOpen(false)}
                onDeleted={() => onChanged?.()}
            />
        </>
    );

    if (!dnd) return view;
    return (
        <div
            draggable
            onDragStart={() => dnd.onDragStart(user)}
            onDragEnd={() => dnd.onDragEnd()}
            style={{ opacity: dnd.draggedUser?.user_id === user.user_id ? 0.4 : 1, cursor: 'grab' }}
        >
            {view}
        </div>
    );
}

//=================================================================================================
interface AdminUserNodeItemProps {
    node: DbNode;
    depth: number;
    onNodeUpdated?: () => void;
}

const AdminUserNodeItem = observer(({ node, depth, onNodeUpdated: _onNodeUpdated }: AdminUserNodeItemProps) => {
    const dnd = useUserDnd();
    const [store] = useState(() => new AdminUserStore(node.node_id));
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState(false);

    const refreshToken = dnd?.refreshToken;
    useEffect(() => {
        if (!loaded) return;
        store.load().catch(() => {});
    }, [refreshToken]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        store
            .load()
            .then(() => setLoaded(true))
            .catch(() => setError(true));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    let content: React.ReactNode;
    if (store.loading && !loaded) {
        content = <NodeChildrenSkeleton />;
    } else if (error) {
        content = <NodeLoadError />;
    } else if (loaded && store.nodes.length === 0 && store.users.length === 0) {
        content = undefined;
    } else {
        content = (
            <>
                {store.nodes.map((n) => (
                    <AdminUserNodeItem key={n.node_id} node={n} depth={depth + 1} onNodeUpdated={() => store.load()} />
                ))}
                {store.users.map((u) => (
                    <UserItem key={u.user_id} user={u} depth={depth + 1} onChanged={() => store.load()} />
                ))}
            </>
        );
    }

    return (
        <NodeView
            name={node.name}
            description={node.description ?? undefined}
            icon={nodeIcon(node.type)}
            depth={depth}
            onDrop={dnd ? () => dnd.onDrop(node.node_id, node.name) : undefined}
        >
            {content}
        </NodeView>
    );
});

//=================================================================================================
function UserManager() {
    const { authStore } = useContext(AuthStoreContext);
    const user = authStore.user;

    const [store] = useState(() => new AdminUserStore(user?.node_id ?? 0));
    const [initialLoading, setInitialLoading] = useState(true);
    const [error, setError] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [draggedUser, setDraggedUser] = useState<DbUser | null>(null);
    const [pendingMove, setPendingMove] = useState<PendingMove | null>(null);
    const [refreshToken, setRefreshToken] = useState(0);

    useEffect(() => {
        if (!user) return;
        store
            .load()
            .catch(() => setError(true))
            .finally(() => setInitialLoading(false));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!user) return null;

    const handleAdded = () => {
        store.load().catch(() => {});
        setRefreshToken((k) => k + 1);
    };

    const handleDrop = (nodeId: number, nodeName: string) => {
        if (!draggedUser || draggedUser.node_id === nodeId) {
            setDraggedUser(null);
            return;
        }
        setPendingMove({ user: draggedUser, targetNodeId: nodeId, targetNodeName: nodeName });
        setDraggedUser(null);
    };

    const handleMoveConfirm = async () => {
        if (!pendingMove) return;
        await UserService.edit({ ...pendingMove.user, node_id: pendingMove.targetNodeId });
        store.load().catch(() => {});
        setRefreshToken((k) => k + 1);
        setPendingMove(null);
    };

    const dndContext = {
        draggedUser,
        refreshToken,
        onDragStart: (u: DbUser) => setDraggedUser(u),
        onDragEnd: () => setDraggedUser(null),
        onDrop: handleDrop,
    };

    let content: React.ReactNode;
    if (initialLoading) {
        content = <NodeChildrenSkeleton />;
    } else if (error) {
        content = <NodeLoadError />;
    } else {
        content = (
            <>
                {store.nodes.map((n) => (
                    <AdminUserNodeItem key={n.node_id} node={n} depth={1} onNodeUpdated={() => store.load()} />
                ))}
                {store.users
                    .filter((u) => u.user_id !== user.user_id)
                    .map((u) => (
                        <UserItem key={u.user_id} user={u} depth={1} onChanged={() => store.load()} />
                    ))}
            </>
        );
    }

    return (
        <UserDndContext.Provider value={dndContext}>
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2 }}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Управление пользователями
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Учётные записи и их расположение в иерархии предприятия
                        </Typography>
                    </Box>
                    {user.isRoot() && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            disableElevation
                            onClick={() => setDialogOpen(true)}
                        >
                            Добавить
                        </Button>
                    )}
                </Box>
                <Divider />
                <List dense disablePadding sx={{ p: 1 }}>
                    <NodeView
                        name="Всё"
                        icon={<LanguageIcon fontSize="small" />}
                        depth={0}
                        defaultOpen
                        onDrop={() => handleDrop(user.node_id, user.login)}
                    >
                        {content}
                    </NodeView>
                </List>
            </Paper>
            {user.isRoot() && (
                <AddUserDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onAdded={handleAdded}
                    rootNodeId={user.node_id}
                />
            )}
            {pendingMove && (
                <MoveUserDialog
                    userLogin={pendingMove.user.login}
                    targetNodeName={pendingMove.targetNodeName}
                    open={Boolean(pendingMove)}
                    onClose={() => setPendingMove(null)}
                    onConfirm={handleMoveConfirm}
                />
            )}
        </UserDndContext.Provider>
    );
}

export default observer(UserManager);
